import React, { useEffect } from "react";
import { useRouter } from "next/router";

import "bootstrap/scss/bootstrap.scss";
import "../public/scss/default/euclid-circulara.scss";

// ========= Plugins CSS START =========
import "../node_modules/sal.js/dist/sal.css";
import "../public/css/plugins/fontawesome.min.css";
import "../public/css/plugins/feather.css";
import "../public/css/plugins/odometer.css";
import "../public/css/plugins/animation.css";
import "../public/css/plugins/euclid-circulara.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-cards";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
// ========= Plugins CSS END =========

import "../public/scss/styles.scss";

import "../public/css/pages/HeaderBlend.css";
import "../public/css/pages/Hero.css";
import "../public/css/pages/AuthSplit.css";
import "../public/css/pages/CourseraNav.css";
import "../public/css/pages/ContactPage.css";
import "../public/css/pages/HeroLight.css";
import "../public/css/pages/HomeSections.css";
import "../public/css/pages/CoursePage.css";
import "../public/css/pages/CourseDetailsPage.css";
import "../public/css/pages/EventDetailsV2.css";
import "../public/css/pages/EventsPage.css";
import "../public/css/pages/FindMyTickets.css";
import "../public/css/pages/DashboardPage.css";
import "../public/css/pages/EventWizard.css";
import "../public/css/pages/AppealPage.css";
import "../public/css/pages/CallToAction.css";
import "../public/css/pages/ClassroomPage.css";
import "../public/css/pages/TutorPage.css";
import "../public/css/pages/CertificatePage.css";
import "../public/css/pages/FaqPage.css";
import "../public/css/pages/LegalPages.css";
import "../public/css/pages/SettingsPage.css";
import "../public/css/pages/RichText.css";
import "../public/css/pages/MobileMenu.css";
import "../public/css/pages/CertificateV2.css";
import "../public/css/pages/CongratulationsPage.css";
import "../public/css/pages/CourseCompletionSummary.css";
import "../public/css/pages/TutorRecruitment.css";
import "../public/css/pages/AdminCourseCollections.css";
import "../public/css/pages/InstructorsPage.css";
import "../public/css/pages/StudentTeachCTA.css";
import "../public/css/pages/EventBookingStats.css";

import Context from "@/context/Context";
import { ProfileProvider } from "@/context/ProfileContext";
import { Toaster } from "sonner";
import { restorePersistedLanguage } from "@/lib/i18n";
import { trackPageVisit } from "@/lib/analytics";

import CookieConsentBanner from "@/my_components/Common/CookieConsentBanner";
import RouteProgressBar from "@/my_components/Common/RouteProgressBar";
import InstallPrompt from "@/my_components/Common/InstallPrompt";

import { ROUTE } from "@/route/app_routes";
import { CONFIG } from "@/app_config.js";

export default function App({ Component, pageProps }) {
  const router = useRouter();

  // Dynamic import() (not require()) -- webpack can only split a require() call's target into
  // its own lazy chunk when it's written as import(), so the old require() here was bundled
  // straight into the shared _app chunk every page pays for on first load, even though nothing
  // needs Bootstrap's JS (dropdowns/offcanvas/etc., not used by this app's own React components)
  // until after mount at the earliest.
  useEffect(() => {
    import("bootstrap/dist/js/bootstrap.bundle.min.js");
  }, []);

  // Visitor beacon (see lib/analytics.js) -- fires once for the initial load and again on every
  // client-side route change, powering the admin funnel's "Visitors" stage. Best-effort only:
  // never blocks navigation and silently no-ops if storage/network is unavailable.
  useEffect(() => {
    trackPageVisit(router.asPath);
    const handleRouteChange = (url) => trackPageVisit(url);
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-applies a persisted language choice (localStorage "smarttech-lang") after mount, once
  // hydration has already matched the server's English-only first render -- see lib/i18n.js's
  // getInitialLanguage()/restorePersistedLanguage() comments for why this can't happen any
  // earlier without causing a hydration mismatch (mirrors context/Context.js's own theme-class
  // restore effect below, same reasoning, same "apply after mount" pattern).
  useEffect(() => {
    restorePersistedLanguage();
  }, []);

  // next-pwa's own auto-injected registration (via its webpack entry hook)
  // doesn't fire reliably on every Next.js/webpack version -- this project's
  // build produces a valid public/sw.js but nothing was ever calling
  // navigator.serviceWorker.register() for it, so the app was never actually
  // installable. Registering it explicitly here is the standard, documented
  // workaround. Only runs in production: next.config.js disables next-pwa
  // in development, so no real service worker exists to register there.
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
  }, []);

  return (
      <Context>
        <ProfileProvider>
          <RouteProgressBar />
          <Toaster
            position="top-right"
            richColors
            closeButton
            theme="system"
            toastOptions={{ duration: 4000 }}
          />

          {/* Used to wrap every page in a framer-motion AnimatePresence(mode="wait") + a
              y/scale/opacity transition. That combination forces every single navigation to sit
              through a mandatory ~0.4-0.9s "wait for the old page to finish exiting before the
              new one is even allowed to mount" sequence -- routing felt sluggish/"cracking"
              site-wide because of it, and it was fighting Next's own instant client-side
              transitions rather than complementing them. Removed outright rather than tuned,
              since a page-transition wrapper is a cost every navigation pays regardless of how
              fast the animation itself is -- RouteProgressBar above already gives a lightweight,
              non-blocking sense of motion on slower navigations without holding anything up. */}
          <Component {...pageProps} />

          <CookieConsentBanner />
          <InstallPrompt />
        </ProfileProvider>
      </Context>
  );

}
